import { useCallback, useMemo } from 'react';
import { useKeyed, useQueryState } from './url';

export type SortDir = 'asc' | 'desc';

/** Rows-per-page choices every table offers unless it says otherwise. */
export const DEFAULT_PAGE_SIZES = [10, 25, 50, 100];

export interface TableState {
  q: string;
  sort: string | null;
  dir: SortDir;
  page: number;
  pageSize: number;
  filters: Record<string, string>;
}

export interface TableStateApi extends TableState {
  setQuery: (q: string) => void;
  setSort: (id: string | null, dir?: SortDir) => void;
  toggleSort: (id: string) => void;
  setPage: (p: number) => void;
  setPageSize: (size: number) => void;
  setFilter: (key: string, value: string | null) => void;
  clear: () => void;
  /** True when any search, filter or non-default sort is active. */
  active: boolean;
}

/**
 * Table view state stored in the URL: `q`, `sort=id:dir`, `page`, `size` (rows per page), plus one key per filter.
 * Changing search/filters/sort resets the page. `prefix` namespaces the keys for pages with two tables.
 */
export function useTableState(filterKeys: string[], opts: { prefix?: string; defaultSort?: { id: string; dir: SortDir }; defaultPageSize?: number; pageSizes?: number[] } = {}): TableStateApi {
  const k = useKeyed(opts.prefix);
  const [sp, set] = useQueryState();
  const q = sp.get(k('q')) ?? '';
  const sortRaw = sp.get(k('sort'));
  const [sort, dir] = useMemo<[string | null, SortDir]>(() => {
    if (sortRaw === null) return [opts.defaultSort?.id ?? null, opts.defaultSort?.dir ?? 'asc'];
    if (sortRaw === '') return [null, 'asc'];
    const i = sortRaw.lastIndexOf(':');
    const id = i > 0 ? sortRaw.slice(0, i) : sortRaw;
    const d = i > 0 ? sortRaw.slice(i + 1) : 'asc';
    return [id, d === 'desc' ? 'desc' : 'asc'];
  }, [sortRaw, opts.defaultSort?.id, opts.defaultSort?.dir]);
  const page = Math.max(1, Number(sp.get(k('page')) ?? '1') || 1);
  const defaultPageSize = opts.defaultPageSize ?? 25;
  const sizeRaw = Number(sp.get(k('size')));
  const pageSize = (opts.pageSizes ?? DEFAULT_PAGE_SIZES).includes(sizeRaw) ? sizeRaw : defaultPageSize;
  const filters = useMemo(() => {
    const out: Record<string, string> = {};
    for (const key of filterKeys) {
      const v = sp.get(k(key));
      if (v) out[key] = v;
    }
    return out;
  }, [sp, k, filterKeys]);

  const setQuery = useCallback((value: string) => set({ [k('q')]: value || null, [k('page')]: null }), [set, k]);
  const setSort = useCallback(
    (id: string | null, d: SortDir = 'asc') => {
      const isDefault = id === (opts.defaultSort?.id ?? null) && d === (opts.defaultSort?.dir ?? 'asc');
      set({ [k('sort')]: isDefault ? null : id === null ? '' : `${id}:${d}`, [k('page')]: null });
    },
    [set, k, opts.defaultSort?.id, opts.defaultSort?.dir],
  );
  const toggleSort = useCallback(
    (id: string) => {
      if (sort !== id) setSort(id, 'asc');
      else if (dir === 'asc') setSort(id, 'desc');
      else setSort(opts.defaultSort?.id ?? null, opts.defaultSort?.dir ?? 'asc');
    },
    [sort, dir, setSort, opts.defaultSort?.id, opts.defaultSort?.dir],
  );
  const setPage = useCallback((p: number) => set({ [k('page')]: p > 1 ? p : null }), [set, k]);
  const setPageSize = useCallback((size: number) => set({ [k('size')]: size === defaultPageSize ? null : size, [k('page')]: null }), [set, k, defaultPageSize]);
  const setFilter = useCallback((key: string, value: string | null) => set({ [k(key)]: value || null, [k('page')]: null }), [set, k]);
  const clear = useCallback(() => {
    const patch: Record<string, null> = { [k('q')]: null, [k('sort')]: null, [k('page')]: null };
    for (const key of filterKeys) patch[k(key)] = null;
    set(patch);
  }, [set, k, filterKeys]);

  const active = q.length > 0 || Object.keys(filters).length > 0 || sortRaw !== null;
  return { q, sort, dir, page, pageSize, filters, setQuery, setSort, toggleSort, setPage, setPageSize, setFilter, clear, active };
}

export type SortValue = string | number | boolean | Date | null | undefined;

/** Stable comparator: nulls last, numbers numeric, strings case-insensitive. */
export function compare(a: SortValue, b: SortValue, dir: SortDir): number {
  const na = a === null || a === undefined || a === '';
  const nb = b === null || b === undefined || b === '';
  if (na && nb) return 0;
  if (na) return 1;
  if (nb) return -1;
  let r: number;
  if (a instanceof Date || b instanceof Date) r = new Date(a as Date).getTime() - new Date(b as Date).getTime();
  else if (typeof a === 'number' && typeof b === 'number') r = a - b;
  else if (typeof a === 'boolean' && typeof b === 'boolean') r = Number(a) - Number(b);
  else r = String(a).localeCompare(String(b), undefined, { sensitivity: 'base', numeric: true });
  return dir === 'asc' ? r : -r;
}

/** Every whitespace-separated token must appear somewhere in the haystack. */
export function matchesQuery(haystack: string, q: string): boolean {
  const tokens = q.toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;
  const h = haystack.toLowerCase();
  return tokens.every((t) => h.includes(t));
}
